import { useParams, useNavigate } from "react-router-dom";
import { EDUCATION_PACKAGES } from "../Education";
import CourseDetailsForm, { CourseDetails } from "../../components/education/CourseDetailsForm";
import BNPLProviderSelection from "@/components/payments/BNPLProviderSelection";
import SmartCheckoutRecommendation from "@/components/payments/SmartCheckoutRecommendation";
import PaymentConfirmation from "@/components/payments/PaymentConfirmation";
import InvoiceUpload from "../../components/InvoiceUpload";
import { suggestFinancePlans } from "../../lib/finance-plans";
import type { ParsedDoc } from "../../lib/invoice-parser";
import { useState } from "react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function CourseBooking() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const pkg = EDUCATION_PACKAGES.find(p => p.id === id);
  const [step, setStep] = useState(1);
  const [details, setDetails] = useState<CourseDetails | null>(null);
  const [planChoice, setPlanChoice] = useState<any>(null);
  const [parsedDoc, setParsedDoc] = useState<ParsedDoc | null>(null);

  if (!pkg) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Course Not Found</h1>
          <Button onClick={() => navigate("/education")}>
            Back to Education
          </Button>
        </div>
      </div>
    );
  }

  const onDetailsSubmit = (d: CourseDetails) => {
    setDetails(d);
    setStep(2);
  };

  const financeAmount = parsedDoc?.amountAED ?? pkg.price;

  const financeOptions = suggestFinancePlans({
    vertical: "education",
    amountAED: financeAmount,
    context: {
      level: pkg.level,
      termCount: pkg.termCount,
      enrolled: details?.enrolled ?? false,
      // Optional: higher ed fields if you later add them to the form
      semester: details?.semester,
      creditHours: details?.creditHours
    }
  });

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Course Enrollment";
      case 2: return "Payment Options";
      case 3: return "Confirm Payment";
      default: return "";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="pt-20">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center gap-4 mb-4">
              <Button variant="outline" size="sm" onClick={() => navigate("/education")}>
                <ArrowLeft className="w-4 h-4" />
                Back to Education
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{getStepTitle()}</h1>
            <p className="text-muted-foreground">{pkg.title}</p>
            <div className="text-sm text-muted-foreground mt-2">
              Annual tuition: AED {pkg.price.toLocaleString()} • {pkg.duration}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12 max-w-4xl">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                1
              </div>
              <div className={`h-1 w-16 ${
                step >= 2 ? "bg-primary" : "bg-muted"
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                2
              </div>
              <div className={`h-1 w-16 ${
                step >= 3 ? "bg-primary" : "bg-muted"
              }`} />
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                3
              </div>
            </div>
          </div>

          {step === 1 && (
            <>
              <CourseDetailsForm
                defaultPriceAED={pkg.price}
                level={pkg.level}
                termCount={pkg.termCount}
                onSubmit={onDetailsSubmit}
              />
              <div className="mt-4">
                <InvoiceUpload onParsed={setParsedDoc} />
                {parsedDoc?.amountAED && (
                  <div className="text-xs text-gray-600 mt-2">
                    Parsed amount: <b>AED {parsedDoc.amountAED.toLocaleString()}</b>
                    {parsedDoc.dueDate ? ` • Due ${parsedDoc.dueDate}` : ""}
                  </div>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <SmartCheckoutRecommendation
              vertical="education"
              amount={financeAmount}
              merchantId={pkg.id}
              merchantProviders={["zendi", "skiply", "tabby", "tamara"]}
              onBack={() => setStep(1)}
              onSelect={(provider, plan) => {
                setPlanChoice({ id: provider, label: provider, ...plan });
                setStep(3);
              }}
            />
          )}

          {step === 3 && details && planChoice && (
            <PaymentConfirmation
              vertical="education"
              amountAED={financeAmount}
              financingPlan={planChoice}
              payerDetails={{
                fullName: details.guardianName,
                email: details.email,
                phone: details.phone
              }}
              onBack={() => setStep(2)}
              onConfirm={() => {
                window.location.href = planChoice.redirectUrl || "/booking-confirmed?via=education";
              }}
            />
          )}
        </main>
      </div>
    </div>
  );
}